import React, { useState, useEffect } from 'react';
import { Modal, Heading, Text } from './ui';
import { createMergeProposal } from '../services/api';
import { useAuth } from '../context/authContext';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const MergeProposalModal = ({ isOpen, onClose, sourceRecipe, onProposalCreated }) => {
    const { user } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        notes: ''
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                title: `Gold Standard Change Proposal for ${sourceRecipe?.name || 'recipe'}`,
                notes: ''
            });
            setError(null);
        } else {
            setFormData({
                title: '',
                notes: ''
            });
        }
    }, [isOpen, sourceRecipe]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            setError('Please fill in all required fields');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const token = await user.getIdToken();
            const proposalData = {
                source_recipe_id: sourceRecipe.id,
                title: formData.title.trim(),
                notes: formData.notes.trim() || null
            };

            const createdProposal = await createMergeProposal(proposalData, token);
            
            if (onProposalCreated) {
                onProposalCreated(createdProposal);
            }
            
            onClose();
        } catch (err) {
            console.error('Error creating merge proposal:', err);
            // Extract error message from different possible error structures
            let errorMessage = 'Failed to create merge proposal';
            if (err.message) {
                errorMessage = err.message;
            } else if (err.error) {
                errorMessage = err.error;
            } else if (typeof err === 'string') {
                errorMessage = err;
            }
            
            // Use the detailed message if available (from backend)
            if (err.message && typeof err.message === 'object' && err.message.message) {
                errorMessage = err.message.message;
            }
            
            // Make error messages more user-friendly
            if (errorMessage.includes('already exists')) {
                errorMessage = 'A merge proposal already exists for this recipe. You can only have one active proposal per recipe pair.';
            } else if (errorMessage.includes('not found')) {
                errorMessage = 'One of the recipes could not be found. Please refresh the page and try again.';
            } else if (errorMessage.includes('unauthorized')) {
                errorMessage = 'You do not have permission to create a merge proposal for this recipe.';
            } else if (errorMessage.includes('validation')) {
                errorMessage = 'Please check your input and try again.';
            }
            
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };


    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Propose Gold Standard Change"
        >
            <div className="space-y-6">
                {/* Context Info */}
                <div className="bg-neutral-50 hairline p-4">
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                            <span className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">PROPOSING CHANGE FOR</span>
                            <span className="font-medium">{sourceRecipe?.name || 'Unknown Recipe'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <span className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">BY</span>
                            <span>{sourceRecipe?.creator_name || 'Unknown'}</span>
                        </div>
                    </div>
                </div>

                {/* Gold Standard Change Tip */}
                <div className="bg-neutral-50 hairline p-4">
                    <Text className="text-sm text-neutral-900 opacity-80">
                        <strong>What's a Gold Standard change?</strong> Propose that your improved version becomes the new Gold Standard for this recipe. 
                        When approved, your version will be shown as the main recipe while keeping the original in the lineage.
                    </Text>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                        <label htmlFor="title" className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                            PROPOSAL TITLE *
                        </label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 bg-neutral-0 text-neutral-900 hairline focus:outline-none focus:ring-2 focus:ring-neutral-900 font-sans text-sm"
                            placeholder="Describe your proposed Gold Standard change"
                            required
                        />
                    </div>

                    {/* Notes */}
                    <div>
                        <label htmlFor="notes" className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                            NOTES
                        </label>
                        <textarea
                            id="notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            rows={4}
                            className="w-full px-3 py-2 bg-neutral-0 text-neutral-900 hairline focus:outline-none focus:ring-2 focus:ring-neutral-900 font-sans text-sm resize-none"
                            placeholder="Explain why your version should become the new Gold Standard and what improvements you made..."
                        />
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 p-4">
                            <div className="flex items-start space-x-3">
                                <div className="text-red-600 text-lg">⚠️</div>
                                <div className="flex-1">
                                    <Text className="text-red-900 font-medium text-sm mb-1">
                                        Unable to Create Gold Standard Change Proposal
                                    </Text>
                                    <Text className="text-red-800 text-sm leading-relaxed mb-3">
                                        {error}
                                    </Text>
                                    {error.includes('already exists') && (
                                        <div className="mt-3">
                                            <Text className="text-red-700 text-xs">
                                                💡 You can view your existing proposal or wait for the current one to be reviewed.
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-sm uppercase tracking-wider"
                            disabled={submitting}
                        >
                            CANCEL
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none text-white hover:opacity-60 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ backgroundColor: colorTokens.oxfordBlue }}
                            disabled={submitting || !formData.title.trim()}
                        >
                            {submitting ? 'SUBMITTING...' : 'PROPOSE GOLD STANDARD CHANGE'}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default MergeProposalModal;
